<template>
  <!-- d-inline-flex, not a block div: this renders inside a b-table actions cell
       beside the report and status buttons, and a block-level root pushed those
       onto a second line. The Excel failure is reported as a toast rather than an
       inline alert for the same reason — an alert in the cell would grow the row
       every time a download fails. -->
  <div class="voucher-export-buttons d-inline-flex align-items-center">
    <b-button size="sm" variant="outline-secondary" class="mr-1" @click="downloadExcel">
      <i class="fas fa-file-excel mr-1"></i>Excel
    </b-button>
    <b-button size="sm" variant="outline-secondary" class="mr-1" @click="openPdfRange">
      <i class="fas fa-id-card mr-1"></i>การ์ด PDF
    </b-button>

    <!-- PDF Range Selection Modal -->
    <b-modal
      ref="pdfRangeModal"
      title="พิมพ์การ์ดบัตรกำนัล"
      ok-title="ดาวน์โหลด"
      cancel-title="ยกเลิก"
      @ok.prevent="downloadPdf"
    >
      <b-form-group label="เริ่มที่ใบ">
        <b-form-input
          v-model.number="from"
          type="number"
          min="1"
          placeholder="1"
        />
      </b-form-group>

      <b-form-group label="ถึงใบ">
        <b-form-input
          v-model.number="to"
          type="number"
          :min="from"
          :placeholder="issued"
        />
      </b-form-group>

      <p class="text-muted mb-1">
        {{ pdfCount }} ใบ = {{ pdfPages }} หน้า A4 ({{ cardsPerPage }} ใบ/หน้า)
      </p>

      <p class="text-muted small mb-3">
        ดาวน์โหลดได้ครั้งละไม่เกิน {{ maxPdfCards }} ใบ ({{ maxPdfPages }} หน้า)
        — ชุดที่ใหญ่กว่านี้ให้ทยอยดาวน์โหลดเป็นช่วง
      </p>

      <b-alert :show="!!rangeError" variant="warning">
        {{ rangeError }}
      </b-alert>

      <b-alert :show="!!downloadError" variant="danger">
        {{ downloadError }}
      </b-alert>

      <p class="text-muted small mb-2">
        ไฟล์มีเส้นตัดและระยะเผื่อตัด 3 มม. ส่งโรงพิมพ์ได้ทันที
      </p>

      <p class="text-muted small">
        ลิงก์ดาวน์โหลดมีอายุ 5 นาที
      </p>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";

const CARDS_PER_PAGE = 8;      // buildCardsPdf.js: COLS 2 x ROWS 4
// MUST equal MAX_CARDS in voucherController/exportCardsPdf.js. A larger value
// here pre-fills the modal with a range the server rejects with a 400, which is
// the normal case for a printed campaign (batches run well past 200). Every
// number the operator reads is derived from this constant, so the two cannot
// drift apart on screen.
const MAX_PDF_CARDS = 200;     // 25 A4 pages — past this, print in batches

export default {
  name: "VoucherExportButtons",
  props: {
    batch: { type: Object, required: true },
  },
  data() {
    return { from: 1, to: null, downloadError: "" };
  },
  computed: {
    issued() { return this.batch.issued || this.batch.quantity || 0; },
    cardsPerPage() { return CARDS_PER_PAGE; },
    maxPdfCards() { return MAX_PDF_CARDS; },
    maxPdfPages() { return MAX_PDF_CARDS / CARDS_PER_PAGE; },
    pdfCount() {
      const to = Number(this.to || this.issued);
      const from = Number(this.from);
      return Math.max(0, to - from + 1);
    },
    pdfPages() { return Math.ceil(this.pdfCount / CARDS_PER_PAGE); },
    rangeError() {
      const from = Number(this.from), to = Number(this.to || this.issued);
      if (!Number.isInteger(from) || from < 1) return "เริ่มที่ใบต้องเป็นจำนวนเต็มตั้งแต่ 1";
      if (!Number.isInteger(to) || to > this.issued) return `ถึงใบต้องไม่เกิน ${this.issued}`;
      if (from > to) return "เริ่มที่ใบต้องไม่เกินถึงใบ";
      if (this.pdfCount > MAX_PDF_CARDS) {
        return `เลือกได้ครั้งละไม่เกิน ${this.maxPdfCards} ใบ (${this.maxPdfPages} หน้า)`;
      }
      return "";
    },
  },
  methods: {
    ...mapActions("moduleVoucher", ["fetchCodesExcelUrl", "fetchCardsPdfUrl"]),

    // Excel is TWO hops, like the PDF below: the endpoint answers { url } for a
    // presigned S3 link and only that link is the workbook. It cannot stream the
    // bytes back through the API — API Gateway hands a body whose content type
    // is not in BinaryMediaTypes back as UTF-8 text, which corrupted every ZIP
    // byte of the xlsx and made Excel refuse to open the download.
    //
    // Navigated in place, not window.open'd: S3 serves the workbook with an
    // xlsx content type, so the browser downloads it and the SPA stays put —
    // no stray blank tab for a file that never renders in one. The download is
    // named after the key's last segment (voucher-PREFIX-ID.xlsx), which the
    // server sets for exactly this reason.
    async downloadExcel() {
      try {
        const url = await this.fetchCodesExcelUrl({
          clinicUrl: this.$route.params.clinicUrl,
          batchId: this.batch.id,
        });
        window.location.href = url;
      } catch (error) {
        // Toast, not this.downloadError: the Excel button has no modal to show an
        // alert in, and the only place left was the table cell (see template).
        this.$bvToast.toast(
          error?.response?.data?.message || "ดาวน์โหลดไฟล์ Excel ไม่สำเร็จ",
          { title: "ดาวน์โหลดไม่สำเร็จ", variant: "danger", solid: true }
        );
      }
    },

    // The PDF is TWO hops: the endpoint returns { url } for a presigned S3 link
    // (Lambda cannot return a 6 MB+ body), and only that link is the PDF.
    //
    // The blank window is opened SYNCHRONOUSLY, before the await. A window.open
    // that happens after an await has lost the user gesture and every popup
    // blocker kills it — which would look to the operator like the button is
    // simply broken.
    async downloadPdf() {
      if (this.rangeError) return;
      const target = window.open("", "_blank");
      try {
        const url = await this.fetchCardsPdfUrl({
          clinicUrl: this.$route.params.clinicUrl,
          batchId: this.batch.id,
          from: Number(this.from),
          to: Number(this.to || this.issued),
        });
        if (target) target.location = url;
        else window.location.href = url; // popup blocked entirely — navigate in place
        this.$refs.pdfRangeModal.hide();
      } catch (error) {
        if (target) target.close();
        this.downloadError =
          error?.response?.data?.message || "สร้างไฟล์การ์ดไม่สำเร็จ กรุณาลองใหม่";
      }
    },
    openPdfRange() {
      this.from = 1;
      this.to = Math.min(this.issued, MAX_PDF_CARDS);
      this.downloadError = "";  // a previous failure must not greet the next open
      this.$refs.pdfRangeModal.show();
    },
  },
};
</script>
